import { Fragment, ReactNode, useId } from "react";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { Avatar } from "../Avatar";
import { VISUALLY_HIDDEN } from "../visuallyHidden";
import s from "./InboxRow.module.scss";

type Props = {
  avatar?: { initials: string };
  /** The line's parts, joined with " · " (e.g. who · what). Wrap user text in <bdi>. */
  parts: ReactNode[];
  /** A second, quieter line (e.g. contact · date). */
  meta?: ReactNode;
  /** The whole row is this one button; its name is the label then the parts ("Review Dovid Katz · 2 changes"). */
  action: { label: string; onClick: () => void };
  unread?: boolean;
  /** The word for unread (said, never colour alone). Needed with `unread`. */
  unreadLabel?: string;
};

/** A compact list row (bell "To approve", boards 15/35): avatar, one line that ends in an ellipsis, "Review ›". */
export const InboxRow = ({ avatar, parts, meta, action, unread, unreadLabel }: Props) => {
  const id = useId();
  const ids = { action: `${id}-a`, parts: `${id}-p`, unread: `${id}-u`, meta: `${id}-m` };
  const describedBy = [unread && unreadLabel ? ids.unread : "", meta ? ids.meta : ""].filter(Boolean).join(" ") || undefined;
  return (
    <button type="button" onClick={action.onClick} aria-labelledby={`${ids.action} ${ids.parts}`} aria-describedby={describedBy}
      className={unread ? `${s.row} ${s.unread}` : s.row}>
      {avatar && <Avatar name={avatar.initials} initials={avatar.initials} />}
      <span className={s.text}>
        <span id={ids.parts} className={s.line}>{parts.map((p, i) => <Fragment key={i}>{i > 0 && " · "}<span className={i === 0 ? s.lead : undefined}>{p}</span></Fragment>)}</span>
        {meta && <span id={ids.meta} className={`${s.line} ${s.meta}`}>{meta}</span>}
      </span>
      {unread && unreadLabel && <><span aria-hidden className={s.dot} /><span id={ids.unread} className={VISUALLY_HIDDEN}>{unreadLabel}</span></>}
      <span id={ids.action} className={s.action}>{action.label}</span>
      <ChevronRightRoundedIcon aria-hidden className={s.chevron} />
    </button>
  );
};
